
import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import WebsiteLogo from "@/components/common/WebsiteLogo";
import { submitAccountRequest } from "@/services/firebaseService";
import { z } from "zod";

// Form validation schema
const requestSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  organizationName: z.string().min(2, "Organization name must be at least 2 characters"),
  organizationWebsite: z.string().url("Please enter a valid URL")
});

const RequestAccountPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [organizationWebsite, setOrganizationWebsite] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Extract the returnUrl from query parameters to pass it back to login page
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get("returnUrl") || "";
  
  const loginPath = returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : "/login";

  const validateForm = () => {
    try {
      requestSchema.parse({
        email,
        organizationName,
        organizationWebsite
      });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path) {
            newErrors[err.path[0]] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log("Submitting account request...");
      // Ensure website has protocol
      let website = organizationWebsite;
      if (!/^https?:\/\//i.test(website)) {
        website = `https://${website}`;
      }
      
      // Submit the account request to Firestore
      await submitAccountRequest({
        email,
        organizationName,
        organizationWebsite: website
      });
      
      setIsLoading(false);
      setIsSubmitted(true);
      toast.success("Account request submitted successfully!");
      console.log("Account request submitted successfully");
    } catch (error) {
      console.error("Error submitting account request:", error);
      setIsLoading(false);
      toast.error("Failed to submit account request. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-kidsbooked-lightGray">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 flex flex-col items-center">
          <WebsiteLogo width={240} className="mb-4" />
          <CardTitle className="text-2xl font-bold">Request an Account</CardTitle>
          {!isSubmitted ? (
            <CardDescription>
              Hello! At this time, KidsBooked Pro accounts require admin approval. If a colleague already has a KidsBooked Pro account, ask them for an invitation. Otherwise, provide the information below to get started. We strive to reply to these requests within 1 business day.
            </CardDescription>
          ) : (
            <CardDescription>
              Thank you! We will review and follow-up, usually within 1 business day.
            </CardDescription>
          )}
        </CardHeader>
        {!isSubmitted ? (
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Your email address
                </label>
                <Input
                  id="email"
                  placeholder="name@example.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={errors.email ? "border-red-500" : ""}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              <div className="space-y-2">
                <label htmlFor="organization-name" className="text-sm font-medium">
                  Organization name
                </label>
                <Input
                  id="organization-name"
                  placeholder="Camp Adventure Unlimited"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  required
                  className={errors.organizationName ? "border-red-500" : ""}
                />
                {errors.organizationName && (
                  <p className="text-sm text-red-500">{errors.organizationName}</p>
                )}
              </div>
              <div className="space-y-2">
                <label htmlFor="organization-website" className="text-sm font-medium">
                  Organization website
                </label>
                <Input
                  id="organization-website"
                  placeholder="https://www.example.com"
                  type="url"
                  value={organizationWebsite}
                  onChange={(e) => setOrganizationWebsite(e.target.value)}
                  required
                  className={errors.organizationWebsite ? "border-red-500" : ""}
                />
                {errors.organizationWebsite && (
                  <p className="text-sm text-red-500">{errors.organizationWebsite}</p>
                )}
              </div>
              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={isLoading}
              >
                {isLoading ? "Submitting..." : "Submit Request"}
              </Button>
            </form>
          </CardContent>
        ) : (
          <CardContent className="text-center py-8">
            <div className="mb-6 bg-green-50 text-green-700 p-4 rounded-md">
              Your request has been submitted. We'll be in touch soon!
            </div>
            <Button 
              onClick={() => navigate(loginPath)}
              className="btn-primary"
            >
              Back to Login
            </Button>
          </CardContent>
        )}
        <CardFooter className="justify-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to={loginPath} className="text-kidsbooked-blue hover:underline">
              Login
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default RequestAccountPage;
